import Cookies from 'js-cookie';
import {ParsedUrlQuery} from 'querystring';

const SOURCE_COOKIE_KEY = 'source_t';
const FALLBACK_SOURCE = 'WEB';
const DEFAULT_TTL_DAYS = 30;

const SOURCE_TTL_DAYS: Record<string, number> = {
    GOOGLE: 30,
    FACEBOOK: 7,
    INSTAGRAM: 7,
};

const SOURCE_ALIASES: Record<string, string> = {
    tiktok: 'TIKTOK',
    instagram: 'INSTAGRAM',
    facebook: 'FACEBOOK',
    fb: 'FACEBOOK',
    google: 'GOOGLE',
    subito: 'SUBITO.IT',
    autoscout24: 'AUTOSCOUT.IT',
    chatgpt: 'CHATGPT',
    rentago: 'RENTAGO',
    messaggero: 'MESSAGGERO',
};

type LeadSourceCookiePayload = {
    source: string;
    capturedAt: string;
    campaign?: string;
    gclid?: string;
    fbclid?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
};

export type LeadMarketingDetails = LeadSourceCookiePayload;

const findAlias = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }

    const lowerValue = value.toLowerCase();
    const aliasKey = Object.keys(SOURCE_ALIASES).find(key => lowerValue.includes(key));

    return aliasKey ? SOURCE_ALIASES[aliasKey] : null;
};

const normalizeSource = (value?: string | null): string => {
    if (!value) {
        return FALLBACK_SOURCE;
    }

    const alias = findAlias(value);

    return alias ?? value.toUpperCase();
};

const normalizeValue = (value?: string | null): string | undefined => {
    const normalized = String(value || "").trim();
    return normalized.length > 0 ? normalized : undefined;
};

const pickValue = (...values: Array<string | undefined | null>) => {
    for (const value of values) {
        const normalized = normalizeValue(value);
        if (normalized) {
            return normalized;
        }
    }
    return undefined;
};

const getTtlForSource = (source: string): number => {
    return SOURCE_TTL_DAYS[source] ?? DEFAULT_TTL_DAYS;
};

const parseLeadSourcePayload = (rawValue?: string): LeadSourceCookiePayload | null => {
    if (!rawValue) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawValue);
        if (parsed && typeof parsed === 'object' && typeof parsed.source === 'string') {
            return {
                source: parsed.source,
                capturedAt: typeof parsed.capturedAt === 'string' ? parsed.capturedAt : new Date().toISOString(),
                campaign: normalizeValue(parsed.campaign ?? parsed.utmCampaign),
                gclid: normalizeValue(parsed.gclid),
                fbclid: normalizeValue(parsed.fbclid),
                utmSource: normalizeValue(parsed.utmSource ?? parsed.utm_source),
                utmMedium: normalizeValue(parsed.utmMedium ?? parsed.utm_medium),
                utmCampaign: normalizeValue(parsed.utmCampaign ?? parsed.utm_campaign),
                utmTerm: normalizeValue(parsed.utmTerm ?? parsed.utm_term),
                utmContent: normalizeValue(parsed.utmContent ?? parsed.utm_content),
            };
        }
    } catch (e) {
        // Ignore and fallback to legacy plain string value
    }

    return {
        source: rawValue,
        capturedAt: new Date().toISOString(),
    };
};

const writeLeadSourcePayload = (payload: LeadSourceCookiePayload) => {
    if (typeof window === 'undefined') {
        return;
    }

    const ttlDays = getTtlForSource(payload.source);

    Cookies.set(SOURCE_COOKIE_KEY, JSON.stringify(payload), {
        expires: ttlDays,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:',
    });
};

export const detectLeadSource = (query: ParsedUrlQuery, referrer: string): string => {
    const sourceFromQuery = findAlias(query?.utm_source?.toString());
    if (sourceFromQuery) {
        return sourceFromQuery;
    }

    if (query?.gclid) {
        return 'GOOGLE';
    }

    if (query?.fbclid) {
        return 'FACEBOOK';
    }

    const sourceFromReferrer = findAlias(referrer);
    if (sourceFromReferrer) {
        return sourceFromReferrer;
    }

    return FALLBACK_SOURCE;
};

export const setLeadSource = (source: string): LeadSourceCookiePayload | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const normalizedSource = normalizeSource(source);
    const payload: LeadSourceCookiePayload = {
        source: normalizedSource,
        capturedAt: new Date().toISOString(),
    };

    writeLeadSourcePayload(payload);
    try {
        window.sessionStorage?.setItem('source_t', normalizedSource);
        window.localStorage?.setItem('source_t', normalizedSource);
    } catch (error) {
        // Ignore storage errors (e.g., Safari private mode)
    }

    return payload;
};

export const setLeadMarketingDetails = (payload: Partial<LeadMarketingDetails>) => {
    if (typeof window === 'undefined') {
        return null;
    }

    const current = getLeadSourceDetails();
    const source = normalizeSource(payload.source || current?.source || FALLBACK_SOURCE);
    const nextPayload: LeadSourceCookiePayload = {
        source,
        capturedAt: new Date().toISOString(),
        campaign: pickValue(payload.campaign, payload.utmCampaign, current?.campaign, current?.utmCampaign),
        gclid: pickValue(payload.gclid, current?.gclid),
        fbclid: pickValue(payload.fbclid, current?.fbclid),
        utmSource: pickValue(payload.utmSource, current?.utmSource),
        utmMedium: pickValue(payload.utmMedium, current?.utmMedium),
        utmCampaign: pickValue(payload.utmCampaign, current?.utmCampaign),
        utmTerm: pickValue(payload.utmTerm, current?.utmTerm),
        utmContent: pickValue(payload.utmContent, current?.utmContent),
    };

    writeLeadSourcePayload(nextPayload);
    try {
        window.sessionStorage?.setItem('source_t', source);
        window.localStorage?.setItem('source_t', source);
    } catch (error) {
        // Ignore storage errors
    }

    return nextPayload;
};

export const getLeadSourceDetails = (): LeadSourceCookiePayload | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const rawValue = Cookies.get(SOURCE_COOKIE_KEY);

    return parseLeadSourcePayload(rawValue);
};

export const getLeadMarketingDetails = (): LeadMarketingDetails | null => {
    return getLeadSourceDetails();
};

export const getLeadSourceValue = (): string => {
    const details = getLeadSourceDetails();

    if (details?.source) {
        return details.source;
    }

    if (typeof window !== 'undefined') {
        const sessionValue = window.sessionStorage?.getItem('source_t');
        if (sessionValue) {
            return normalizeSource(sessionValue);
        }
    }

    return FALLBACK_SOURCE;
};

export const ensureLeadSource = (source: string) => {
    const normalizedSource = normalizeSource(source);
    const current = getLeadSourceDetails();

    if (!current || current.source !== normalizedSource) {
        setLeadSource(normalizedSource);
        return;
    }

    // Refresh TTL when source matches current one
    setLeadSource(normalizedSource);
};

type LeadSourceQueryRecord = Record<string, string | undefined>;

const toQueryRecord = (query?: LeadSourceQueryRecord): Record<string, string> => {
    if (!query) {
        return {};
    }

    return Object.entries(query).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value != null) {
            acc[key] = String(value);
        }
        return acc;
    }, {});
};

type EvaluateLeadSourceOptions = {
    query?: LeadSourceQueryRecord;
    referrer?: string;
    sessionValue?: string | null;
};

const logLeadSourceEvaluation = (payload: unknown) => {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('Lead source evaluation', payload);
    }
};

const refreshLeadSource = (source: string | null) => {
    if (!source) {
        return;
    }

    setLeadSource(source);
};

const buildLeadMarketingPayload = (
    query: Record<string, string>,
    detectedSource: string,
    currentDetails: LeadSourceCookiePayload | null,
): LeadSourceCookiePayload => ({
    source: detectedSource !== FALLBACK_SOURCE
        ? detectedSource
        : currentDetails?.source || FALLBACK_SOURCE,
    capturedAt: new Date().toISOString(),
    campaign: pickValue(query.utm_campaign, currentDetails?.campaign, currentDetails?.utmCampaign),
    gclid: pickValue(query.gclid, currentDetails?.gclid),
    fbclid: pickValue(query.fbclid, currentDetails?.fbclid),
    utmSource: pickValue(query.utm_source, currentDetails?.utmSource),
    utmMedium: pickValue(query.utm_medium, currentDetails?.utmMedium),
    utmCampaign: pickValue(query.utm_campaign, currentDetails?.utmCampaign),
    utmTerm: pickValue(query.utm_term, currentDetails?.utmTerm),
    utmContent: pickValue(query.utm_content, currentDetails?.utmContent),
});

export const evaluateLeadSourceFromQuery = (options: EvaluateLeadSourceOptions = {}) => {
    if (typeof window === 'undefined') {
        return;
    }

    const query = toQueryRecord(options.query);
    const referrer = (options.referrer || '').toLowerCase();
    const detectedSource = detectLeadSource(query as ParsedUrlQuery, referrer);
    const sessionValue = options.sessionValue ?? window.sessionStorage?.getItem(SOURCE_COOKIE_KEY) ?? null;
    const currentDetails = getLeadSourceDetails();
    const currentSource = currentDetails?.source ?? null;

    logLeadSourceEvaluation({
        query,
        referrer,
        detectedSource,
        sessionValue,
        currentSource,
    });

    if (!currentSource) {
        const initialSource = detectedSource !== FALLBACK_SOURCE
            ? detectedSource
            : sessionValue || FALLBACK_SOURCE;

        setLeadMarketingDetails(buildLeadMarketingPayload(query, initialSource, currentDetails));
        return;
    }

    if (detectedSource !== FALLBACK_SOURCE) {
        if (detectedSource !== currentSource) {
            setLeadMarketingDetails(buildLeadMarketingPayload(query, detectedSource, currentDetails));
            return;
        }

        setLeadMarketingDetails(buildLeadMarketingPayload(query, currentSource, currentDetails));
        return;
    }

    const normalizedSession = sessionValue ? normalizeSource(sessionValue) : null;

    if (normalizedSession && normalizedSession !== currentSource) {
        setLeadMarketingDetails(buildLeadMarketingPayload(query, normalizedSession, currentDetails));
        return;
    }

    setLeadMarketingDetails(buildLeadMarketingPayload(query, currentSource, currentDetails));
};

export const evaluateLeadSourceFromLocation = (
    extraQuery?: LeadSourceQueryRecord,
    referrerOverride?: string,
) => {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);
    const searchParamsRecord = Array.from(url.searchParams.entries()).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = value;
        return acc;
    }, {});
    const referrerValue = referrerOverride ?? document.referrer ?? '';
    let referrerQuery: Record<string, string> = {};

    try {
        if (referrerValue) {
            const referrerUrl = new URL(referrerValue);
            referrerQuery = Array.from(referrerUrl.searchParams.entries()).reduce<Record<string, string>>((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});
        }
    } catch {
        referrerQuery = {};
    }

    const mergedQuery: LeadSourceQueryRecord = {
        ...referrerQuery,
        ...searchParamsRecord,
        ...extraQuery,
    };

    evaluateLeadSourceFromQuery({
        query: mergedQuery,
        referrer: referrerValue,
    });
};
